'use strict'

const { NS_RE, specRefUrl, parseMetaRef, scalar, normList } = require('./spec-ref')

// Same phases and labels the board's columns use.
const PHASES = {
  draft: 'Draft',
  'ready-for-review': 'Ready for review',
  'in-review': 'In review',
  approved: 'Approved',
  implemented: 'Implemented'
}

// postProcess runs after preventXSS, so nothing here is sanitized downstream.
function esc (s) {
  return String(s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

function refLink (ref, ctx) {
  // A shortid names a spec with no number to address, so it can only be
  // reached as a note.
  const href = ref.noteId
    ? (ctx.serverURL || '') + '/' + ref.noteId
    : specRefUrl(ref.ns, ref.n, ctx.boardURL)
  if (!href) return esc(ref.label)
  const title = ref.noteId ? 'Spec note ' + ref.noteId : 'Spec ' + ref.ns + '#' + ref.n
  return `<a class="spec-ref" href="${esc(href)}" title="${esc(title)}">${esc(ref.label)}</a>`
}

function refGroup (label, values, ctx) {
  const refs = values.map(v => parseMetaRef(v, ctx.ns)).filter(Boolean)
  if (!refs.length) return ''
  return `<span class="spec-header-rel">${esc(label)} ${refs.map(r => refLink(r, ctx)).join(', ')}</span>`
}

const tagsOf = meta => (meta ? normList(meta.tags).map(t => t.toLowerCase()) : [])

// The one definition of "this note is a spec", shared with the caller so the
// gate that fetches and the gate that renders cannot drift apart.
function isSpecMeta (meta) {
  return tagsOf(meta).includes('spec')
}

const fact = (text, cls, title) =>
  `<span class="spec-header-fact${cls ? ' ' + cls : ''}"${title ? ` title="${esc(title)}"` : ''}>${esc(text)}</span>`

function prLink (rec, ns) {
  // The number is re-checked here, not trusted from the record: this is the one
  // value that lands in an href without passing through esc.
  const n = rec && Number(rec.pr)
  if (!Number.isInteger(n) || n <= 0) return ''
  const state = rec.prState || 'open'
  const label = `#${n}` + (state === 'merged' || state === 'closed' ? ' ' + state : '')
  // Only a real owner/repo becomes a link, the same gate specRefUrl applies.
  // Anything else would aim the spec's own number at an arbitrary github path.
  if (!NS_RE.test(ns)) return `<span class="spec-pr">${esc(label)}</span>`
  return `<a class="spec-pr pr-${esc(state)}" href="https://github.com/${esc(ns)}/pull/${n}"` +
    ` target="_blank" rel="noopener">${esc(label)}</a>`
}

// The board's changes page for this note. The path was allowlisted when the
// record was normalised; the origin is configuration. Reads "changed since
// your approval" for a viewer the board says the text moved past.
function movedPast (rec, me) {
  return !!me && !!rec && Array.isArray(rec.stale) && rec.stale.some(a => String(a).toLowerCase() === String(me).toLowerCase())
}

// The one place the href is built. Anchored at the viewer's own approval
// when the board says the text moved past it.
function changesHref (rec, boardURL, me) {
  if (!rec || !/^\/changes\/[\w-]{1,128}$/.test(String(rec.changesUrl || '')) || !boardURL) return ''
  return String(boardURL).replace(/\/+$/, '') + rec.changesUrl + (movedPast(rec, me) ? '?from=approval:' + encodeURIComponent(me) : '')
}

function changesLink (rec, boardURL, me) {
  const href = changesHref(rec, boardURL, me)
  if (!href) return ''
  const mine = movedPast(rec, me)
  return `<a class="spec-changes${mine ? ' spec-changes-stale' : ''}" href="${esc(href)}" target="_blank" rel="noopener">` +
    esc(mine ? 'changed since your approval' : 'what changed') + '</a>'
}

// roles.yml can route an undeclared `area:` to a tag or to nowhere, so the
// board's answer wins outright once it exists. A declared value it routed away
// from is shown marked, rather than dropped or believed.
function areaHtml (rec, declared) {
  const area = (rec && rec.area) || declared
  if (!area) return ''
  if (!rec || rec.area) return fact(area)
  return fact(area, 'spec-header-unrouted',
    'Not a declared area, so this spec publishes with no area subdir')
}

// HedgeDoc's meta block rule emits no tokens, so a spec's title, phase and
// declared relationships are invisible in the rendered view. Put them back as a
// header, from the frontmatter plus whatever the board has said about the note.
//
// No h1-h6 here: a heading would join the table of contents and take an anchor
// id, which this is not.
function specHeaderHtml (meta, boardURL, serverURL, record, me) {
  const tags = tagsOf(meta)
  if (!tags.includes('spec')) return ''
  const rec = record && typeof record === 'object' ? record : null

  // Resolved once: the board fills in the namespace a note leaves implicit, and
  // the facts row and the PR link must not disagree about it.
  const ns = (rec && NS_RE.test(String(rec.namespace)) ? rec.namespace : '') || scalar(meta.namespace)
  const ctx = { ns, boardURL, serverURL }
  const owner = scalar(meta.owner)
  // The more advanced of the two wins. The board carries `implemented`, which no
  // frontmatter can, but its answer is pinned at page load, so a tag the author
  // has just advanced past it has to still count.
  const order = Object.keys(PHASES)
  const tagged = order.filter(p => tags.includes(p)).pop() || ''
  const said = rec && Object.prototype.hasOwnProperty.call(PHASES, rec.status) ? rec.status : ''
  const phase = order.indexOf(said) > order.indexOf(tagged) ? said : tagged

  // A top-level spec has no area: it publishes at the specs-dir root and every
  // other spec inherits it.
  const topLevel = String(scalar(meta.kind)).trim().toLowerCase() === 'top-level'
  const facts = [
    phase ? `<span class="spec-status spec-status-${esc(phase)}">${esc(PHASES[phase])}</span>` : '',
    topLevel ? fact('top-level', 'spec-header-kind', 'Constraints every spec in the repo inherits') : '',
    prLink(rec, ns),
    owner ? fact(owner) : '',
    ns ? fact(ns) : '',
    topLevel ? '' : areaHtml(rec, scalar(meta.area)),
    changesLink(rec, boardURL, me)
  ].join('')

  const rels = [
    // supersedes is single-valued on the board, so it is not split on commas.
    refGroup('supersedes', [scalar(meta.supersedes)].filter(Boolean), ctx),
    refGroup('depends on', normList(meta['depends-on']), ctx)
  ].join('')

  const titleText = scalar(meta.title)
  const title = titleText
    ? `<div class="spec-header-title">${esc(titleText)}</div>`
    : ''
  if (!title && !facts && !rels) return ''
  return `<div id="spec-header" class="spec-header">${title}` +
    (facts ? `<div class="spec-header-facts">${facts}</div>` : '') +
    (rels ? `<div class="spec-header-rels">${rels}</div>` : '') +
    '</div>'
}

module.exports = { specHeaderHtml, isSpecMeta, changesHref, movedPast }
